import React from 'react';
import { Form, Input, Select, Switch, Button, Card, DatePicker, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from 'react-query';
import { ValidationTaskV3 } from '../../../services/dataService';
import { CheckCircle2Icon } from 'lucide-react';

const { Option } = Select;

const ImpactType = [
  { label: 'Positive', value: 'Positive' },
  { label: 'Negative', value: 'Negative' },
  { label: 'Neutral', value: 'Neutral' },
  { label: 'Inconclusive', value: 'Inconclusive' },
];

const NCCN_TISSUE_TYPES = [
  'Acute Lymphoblastic Leukemia',
  'Acute Myeloid Leukemia',
  'Ampullary Adenocarcinoma',
  'Anal Carcinoma',
  'Basal Cell Skin Cancer',
  'B-Cell Lymphomas',
  'Biliary Tract Cancers',
  'Bladder Cancer',
  'Bone Cancer',
  'Breast Cancer',
  'Central Nervous System Cancers',
  'Cervical Cancer',
  'Chronic Lymphocytic Leukemia/Small Lymphocytic Lymphoma',
  'Chronic Myeloid Leukemia',
  'Colon Cancer',
  'Dermatofibrosarcoma Protuberans',
  'Esophageal and Esophagogastric Junction Cancers',
  'Gastric Cancer',
  'Gastrointestinal Stromal Tumors',
  'Gestational Trophoblastic Neoplasia',
  'Hairy Cell Leukemia',
  'Head and Neck Cancers',
  'Hepatocellular Carcinoma',
  'Histiocytic Neoplasms',
  'Hodgkin Lymphoma',
  'Kaposi Sarcoma',
  'Kidney Cancer',
  'Melanoma: Cutaneous',
  'Melanoma: Uveal',
  'Merkel Cell Carcinoma',
  'Mesothelioma: Peritoneal',
  'Mesothelioma: Pleural',
  'Multiple Myeloma',
  'Myelodysplastic Syndromes',
  'Myeloid/Lymphoid Neoplasms with Eosinophilia and' + 'Tyrosine Kinase Gene Fusions',
  'Myeloproliferative Neoplasms',
  'Neuroblastoma',
  'Neuroendocrine and Adrenal Tumors',
  'Non-Small Cell Lung Cancer',
  'Ovarian Cancer/Fallopian Tube Cancer/Primary Peritoneal Cancer',
  'Pancreatic Adenocarcinoma',
  'Penile Cancer',
  'Primary Cutaneous Lymphomas',
  'Prostate Cancer',
  'Rectal Cancer',
  'Small Bowel Adenocarcinoma',
  'Small Cell Lung Cancer',
  'Soft Tissue Sarcoma',
  'Squamous Cell Skin Cancer',
  'Systemic Mastocytosis',
  'T-Cell Lymphomas',
  'Testicular Cancer',
  'Thymomas and Thymic Carcinomas',
  'Thyroid Carcinoma',
  'Uterine Neoplasms',
  'Vaginal Cancer',
  'Vulvar Cancer',
  'Waldenström Macroglobulinemia/Lymphoplasmacytic Lymphoma',
  'Wilms Tumor (Nephroblastoma)',
  'N/A',
];

const TissueTypes = [
  'Lymphoid',
  'Skin',
  'Soft tissue',
  'Bowel',
  'Esophagus/stomach',
  'Hematologic not specified',
  'CNS/brain',
  'Multiple tumor types',
  'Breast',
  'Prostate',
  'Thymus',
  'Kidney',
  'Bladder/urinary tract',
  'Myeloid',
  'Liver',
  'Thyroid',
  'Other',
  'Peripheral nervous system',
  'Lung',
  'Pancreas',
  'Adrenal gland',
  'Eye',
  'Head and neck',
  'Cervix',
  'Bone',
  'Ovary/fallopian tube',
  'Biliary tract',
  'Testis',
  'Pleura',
  'Uterus',
  'Penis',
  'Vulva',
  'Ampulla of vater',
  'Peritoneum',
];

const DrugSpecificForm = ({ initialValues, validationTaskId, validator, articleId, isValidated }) => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const saveMutation = useMutation(
    (values) => {
      return ValidationTaskV3.saveDrugAnnotation(validationTaskId, {
        ...values,
        article_id: articleId,
        validation_task_id: validationTaskId,
        validator,
      });
    },
    {
      onSuccess: () => {
        message.success('Annotation saved successfully');
        queryClient.invalidateQueries('validation_task');
      },
    },
  );

  const onFinish = (values) => {
    // console.log('Form values:', values);
    // Handle form submission here
    saveMutation.mutate(values);
  };

  const clearForm = () => {
    form.resetFields();
    form.setFieldsValue({
      ...initialValues,
    });
  };
  return (
    <Card
      title={
        <div className="flex justify-between">
          <div className={isValidated ? 'text-green-500 flex items-center gap-2' : ''}>
            Drug-Specific Validation Form
            {isValidated && <CheckCircle2Icon className="w-4 h-4" />}
          </div>
          <Button onClick={clearForm} type="link" className="text-red-500 hover:text-red-600">
            Clear
          </Button>
        </div>
      }
      style={{ maxWidth: 800, margin: '20px auto' }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          ...initialValues,
        }}
      >
        <div className="flex flex-row gap-4 justify-between">
          <Form.Item name="is_relevant" label="Is Relevant" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item name="first_level_check" label="First Level Check" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item name="is_drug_evaluated" label="Drug eval. for disease?" valuePropName="checked">
            <Switch />
          </Form.Item>
        </div>

        <Form.Item
          name="impact_type"
          label="Impact Type"
          rules={[{ required: true, message: 'Please select impact type!' }]}
        >
          <Select>
            {ImpactType.map((impact) => (
              <Option key={impact.value} value={impact.value}>
                {impact.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="is_combination_therapy" label="Is Combination Therapy" valuePropName="checked">
          <Switch />
        </Form.Item>

        <Card title="Task Specific Details" style={{ marginBottom: 16 }}>
          <Form.Item
            name={['task_specific_details', 'primary_tissue_type']}
            label="Cancer Type"
            rules={[{ required: true, message: 'Please input cancer type!' }]}
          >
            <Select>
              {TissueTypes.map((tissue) => (
                <Option key={tissue} value={tissue}>
                  {tissue}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name={['task_specific_details', 'nccn_cancer_types']} label="NCCN Cancer Types">
            <Select mode="multiple" allowClear>
              {NCCN_TISSUE_TYPES.map((tissue) => (
                <Option key={tissue} value={tissue}>
                  {tissue}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Card>

        <Form.Item>
          <Button type="primary" htmlType="submit">
            Submit
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default DrugSpecificForm;
